import React, { useState, useEffect } from 'react';
import { Database, AlertCircle } from 'lucide-react';
import { checkSupabaseConnection, verifyDatabaseSchema } from '../lib/supabase';

const SupabaseConnectionBanner: React.FC = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isSchemaValid, setIsSchemaValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // First check connection
        const connected = await checkSupabaseConnection();
        setIsConnected(connected);

        if (connected) {
          // Then verify schema
          const schemaValid = await verifyDatabaseSchema();
          setIsSchemaValid(schemaValid);

          if (!schemaValid) {
            setError('Database schema validation failed. Please ensure all required tables exist.');
          }
        } else {
          setError('Could not connect to Supabase database. Please click "Connect to Supabase" to set up your database connection.');
        }
      } catch (err) {
        console.error('Error checking database connection:', err);
        setError('An error occurred while checking the database connection.');
      } finally {
        setIsLoading(false);
      }
    };

    checkConnection();

    // Only keep polling while not yet connected
    const intervalId = setInterval(async () => {
      const connected = await checkSupabaseConnection();
      if (connected) {
        const schemaValid = await verifyDatabaseSchema();
        if (connected && schemaValid) {
          clearInterval(intervalId);
          setIsConnected(true);
          setIsSchemaValid(true);
        }
      }
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  if (isLoading || (isConnected && isSchemaValid)) {
    return null;
  }

  const message = error || 'Could not connect to the database.';
  const hint = isConnected && !isSchemaValid
    ? 'Connected, but some required tables are missing. Run the latest migrations.'
    : 'Check that VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set correctly in .env.';

  return (
    <div className="fixed bottom-16 left-0 right-0 bg-yellow-100 border-t border-yellow-200 p-4 flex justify-between items-center z-50">
      <div className="flex items-center">
        {error ? (
          <AlertCircle size={20} className="text-red-600 mr-2" />
        ) : (
          <Database size={20} className="text-yellow-700 mr-2" />
        )}
        <div>
          <p className="text-sm font-medium text-yellow-800">{message}</p>
          <p className="text-xs text-yellow-700">{hint}</p>
        </div>
      </div>
    </div>
  );
};

export default SupabaseConnectionBanner;